/**
 * AttachedHighlightsChips — Pinned highlight previews above the composer.
 *
 * Reads from `AttachedHighlightsContext`. Renders one small dismissable
 * chip per highlight. When the queue is empty this returns null so the
 * composer layout stays unchanged for normal sends.
 *
 * Intentionally a sibling of `<ChatInput />` rather than a feature inside
 * it — keeps the diff small and means non-Ask-Argus surfaces that also
 * use ChatInput don't accidentally gain this behaviour.
 */

import { X, Quote } from 'lucide-react';
import { useAttachedHighlights } from '@/contexts/attached-highlights-context';

export function AttachedHighlightsChips() {
  const { highlights, remove } = useAttachedHighlights();
  if (highlights.length === 0) return null;

  return (
    <div className="border-t border-border/40 bg-background px-4 pt-3 pb-1 shrink-0">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Quote className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
          {highlights.length === 1
            ? '1 highlight attached'
            : `${highlights.length} highlights attached`}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {highlights.map((h) => (
          <div
            key={h.id}
            className="group flex items-center gap-1.5 max-w-md bg-primary/5 border border-primary/20 rounded-md pl-2 pr-1 py-1 text-xs"
            title={h.text}
          >
            <span className="text-muted-foreground/60 shrink-0">"</span>
            <span className="truncate text-foreground/80">{h.text}</span>
            <span className="text-muted-foreground/60 shrink-0">"</span>
            <button
              type="button"
              onClick={() => remove(h.id)}
              className="h-4 w-4 rounded shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted flex items-center justify-center"
              aria-label="Remove highlight"
              title="Remove"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
